import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, Users, ClipboardCheck, GraduationCap, FileText, AlertTriangle, Heart, Crown } from 'lucide-react';
export function ProfessionalComparison() {
  const comparisons = [{
    situation: 'La abuela hace lo contrario a vos',
    without: 'No sabés hasta que el nene se confunde',
    with: 'Acuerdo de crianza coherente escrito para toda la familia'
  }, {
    situation: 'El jardín dice que "algo no cierra"',
    without: 'Reunión que genera angustia sin solución',
    with: 'Cuaderno de enlace con datos claros cada semana'
  }, {
    situation: 'El pediatra pide un informe de desarrollo',
    without: '3 horas buscando cómo redactarlo',
    with: 'Formato completo de 4 secciones listo para completar'
  }, {
    situation: 'El papá y la mamá no acuerdan límites',
    without: 'Conflicto, inconsistencia, el nene lo percibe',
    with: 'Protocolo de acuerdo con script para 5 conversaciones difíciles'
  }, {
    situation: 'El nene cambia de cuidador de un día para otro',
    without: 'Caos, regresiones, angustia',
    with: 'Ficha de traspaso y protocolo de adaptación listos'
  }, {
    situation: 'La fono y la maestra no están alineadas',
    without: 'Cada una trabaja para su lado',
    with: 'Scripts específicos para 4 situaciones de desalineación'
  }];
  const modules = [{
    icon: <Crown className="w-6 h-6" data-id="element-250" />,
    title: 'Liderazgo en la Crianza',
    color: 'text-amber-300',
    bgColor: 'bg-amber-500/20',
    items: 'El modelo de las 24 horas · Mapa del entorno del niño · Los 3 acuerdos fundamentales · La diferencia entre acompañar y controlar'
  }, {
    icon: <Users className="w-6 h-6" data-id="element-251" />,
    title: 'Gestión del Entorno Familiar',
    color: 'text-sky-300',
    bgColor: 'bg-sky-500/20',
    items: 'Manual para abuelos y cuidadores · Algoritmo de consecuencia natural · Ficha del niño para el cuidador · Formulario de feedback'
  }, {
    icon: <Heart className="w-6 h-6" data-id="element-252" />,
    title: 'Integración Familiar',
    color: 'text-rose-300',
    bgColor: 'bg-rose-500/20',
    items: 'Matriz de prioridades en el hogar · Protocolo de tarea semanal · Acuerdo de crianza familiar · Scripts para 5 conversaciones difíciles'
  }, {
    icon: <GraduationCap className="w-6 h-6" data-id="element-253" />,
    title: 'Coordinación con el Jardín',
    color: 'text-blue-300',
    bgColor: 'bg-blue-500/20',
    items: 'Cuaderno de enlace estructurado · Reunión trimestral con agenda · Scripts para docentes que no acompañan'
  }, {
    icon: <FileText className="w-6 h-6" data-id="element-254" />,
    title: 'Documentación del Desarrollo',
    color: 'text-emerald-300',
    bgColor: 'bg-emerald-500/20',
    items: 'Informe trimestral para el pediatra · Informe anual de logros · Formatos de derivación y seguimiento'
  }, {
    icon: <AlertTriangle className="w-6 h-6" data-id="element-255" />,
    title: 'Gestión de Conflictos entre Adultos',
    color: 'text-orange-300',
    bgColor: 'bg-orange-500/20',
    items: '4 conflictos frecuentes con script completo · Protocolo de crisis del entorno · Manejo de desacuerdos con especialistas'
  }, {
    icon: <ClipboardCheck className="w-6 h-6" data-id="element-256" />,
    title: 'Autocuidado del Cuidador',
    color: 'text-purple-300',
    bgColor: 'bg-purple-500/20',
    items: 'Los 5 signos de agotamiento parental · Sistema de límites operativos · Checklist mensual de bienestar para mamás y papás'
  }];
  return <section className="py-20 bg-sky-950" data-id="element-257">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-258">
        <div className="text-center mb-12" data-id="element-259">
          <h2 className="md:text-4xl font-extrabold text-white mb-4 text-[20px]" data-id="element-260">
            🎯 ¿QUÉ AGREGA EL "SISTEMA MAESTRO DE CRIANZA"?
          </h2>
          <p className="text-sky-100 text-[14px]" data-id="element-261">
            La diferencia entre acompañar solo y construir un entorno que
            multiplica el desarrollo.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="mb-16 overflow-x-auto pb-4" data-id="element-262">
          <div className="min-w-[800px]" data-id="element-263">
            <div className="grid grid-cols-3 gap-4 mb-4 px-4" data-id="element-264">
              <div className="font-bold text-sky-300 uppercase text-sm tracking-wider" data-id="element-265">
                Situación real
              </div>
              <div className="font-bold text-red-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-266">
                <X className="w-4 h-4" data-id="element-267" /> Sin Sistema Maestro
              </div>
              <div className="font-bold text-emerald-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-268">
                <Check className="w-4 h-4" data-id="element-269" /> Con Sistema Maestro
              </div>
            </div>

            <div className="space-y-3" data-id="element-270">
              {comparisons.map((row, i) => <div key={i} className="grid grid-cols-3 gap-4 bg-sky-900/60 rounded-xl p-4 items-center border border-sky-800" data-id="element-271">
                  <div className="font-medium text-white" data-id="element-272">{row.situation}</div>
                  <div className="text-sky-200 text-sm" data-id="element-273">{row.without}</div>
                  <div className="text-emerald-200 font-medium text-sm bg-emerald-500/15 p-2 rounded-lg border border-emerald-500/30" data-id="element-274">
                    {row.with}
                  </div>
                </div>)}
            </div>
          </div>
          <p className="text-center text-sm text-sky-400 mt-4 md:hidden" data-id="element-275">
            ← Scroll to see full table →
          </p>
        </div>

        {/* Modules Grid */}
        <div className="bg-black rounded-3xl p-8 md:p-12 text-white shadow-xl border border-sky-900" data-id="element-276">
          <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white" data-id="element-277">
            <span className="bg-emerald-500 text-white p-1 rounded-full" data-id="element-278">
              <Check className="w-5 h-5" data-id="element-279" />
            </span>
            EL SISTEMA MAESTRO INCLUYE:
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-280">
            {modules.map((mod, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="bg-sky-950/50 rounded-2xl p-6 border border-sky-900" data-id="element-281">
                <div className="flex items-center gap-3 mb-4" data-id="element-282">
                  <div className={`p-2 rounded-lg ${mod.bgColor} ${mod.color}`} data-id="element-283">
                    {mod.icon}
                  </div>
                  <h4 className="font-bold text-lg text-white" data-id="element-284">{mod.title}</h4>
                </div>
                <p className="text-sky-100 text-sm leading-relaxed" data-id="element-285">
                  {mod.items}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}